/* eslint-disable max-lines */
import * as React from 'react';

import { TOffersProps } from './index';
import { prepareOfferCardComponentsProps } from './offer_card';
import { trackFavoriteAdded, trackFavoriteRemoved, trackPageJumped, trackPhoneOpened } from './tracking';
import { OfferContainer } from '../../../../fragments/OfferCardWidget/shared/containers/OfferContainer';
import { isNewbuildingBookedFromDeveloper } from '../../../../fragments/OfferCardWidget/shared/utils/newbuildingBooking';
import { SuburbanBuildersProjectsContainer } from '../../../containers/SuburbanBuildersProjectsContainer';
import { IJsonQuery, IJsonQueryUnderground } from '../../../json_query';
import { IBreadcrumb, IOffer } from '../../../offer/types';
import { trackHiddenObjects } from '../../../tracking/hide_offer';
import { IAdfoxPuidMap } from '../../../types/adfox';
import { ISuburbanProjectSuggest } from '../../../types/suburbanBuildersProjects';
import { isDealRentDisablePhonesExperimentActive } from '../../../utils/experiments/dealRentDisablePhones';
import { isRosreestrCheckedLabelAvailable } from '../../../utils/isRosreestrCheckedLabelAvailable';
import { isSaleFromSuburbanBuilder } from '../../../utils/isSaleFromSuburbanBuilder';
import { IMakeRequest } from '../../../utils/request';
import { shouldSendAdsEvent } from '../../../utils/shouldSendAdsEvent';
import { getOfferKpAdvantages } from '../../../utils/trackings';
import { BannerContainer } from '../../containers/Banner';
import { EHideOfferState } from '../../state/hide_offer';
import { CountrysideTrapBanner } from '../CountrysideTrapBanner';
import { addAdFoxBanner } from '../adfox';

type TSuggestionFragment = { isSimilar: false } | { isSimilar: true; startIndex: number };
export interface IIntegrateAdOptions {
  currentPageNumber: number;
  perPage: number;
  jsonQuery: IJsonQuery;
  offers: IOffer[];
  offerCardCreator(
    offer: IOffer,
    position: number,
    index: number,
    currentPageNumber: number,
    keyPrefix: string,
  ): React.ReactNode;
  isPrintEnabled: boolean;
  suggestionFragment: TSuggestionFragment;
  makeRequest: IMakeRequest;
  hiddenOffers?: number[];
  hiddenSuggestions?: number[];
  animatingOffer?: number | null;
  locationId?: number;
  positionOffset?: number;
  extendedOffersOffset?: number;
  priceRange?: string;
  mlRankingGuid: string | null;
  mlRankingModelVersion: string | null;
  isAgent?: boolean;
  villagesLink?: string;
  showCountrysideTrap?: boolean;
  keyPrefix: string;
  isQaMode?: boolean;
  trackShowNBMotivationPopup?: boolean;
  motivationPopupPosition?: number;
  realtyUserId?: number | null;
  isCountrysideBuildersBannerAvailable?: boolean;
  isFilterByFlatsEnabled?: boolean;
  puidsByFilter?: IAdfoxPuidMap | null;
  servicesBannerPositions: number[];
  showSuburbanListingBanner?: boolean;
  suburbanBuildersProjects?: ISuburbanProjectSuggest[];
}

/**
 * Требования к позициям банsнеров
 * @see https://conf.cian.tech/pages/viewpage.action?pageId=1319907301
 */
const TGP_BANNER_1_POSITION_OFFSET = 5;
const PARALLAX_BANNER_1_POSITION_OFFSET = 10;
const MORTGAGE_BANNER_POSITION_OFFSET = 15;
const PARALLAX_BANNER_2_POSITION_OFFSET = 20;
const TGP_BANNER_2_POSITION_OFFSET = 25;
const SUBURBAN_LISTING_BANNER_POSITION_OFFSET = 7;
const SUBURBAN_BUILDERS_PROJECTS_POSITION_OFFSET = 12;
/**
 * position !== positionOffset
 */
const COUNTRYSIDE_TRAP_BANNER_POSITION = 1;

export const integrateAd = (options: IIntegrateAdOptions) => {
  const {
    currentPageNumber,
    perPage,
    offers,
    offerCardCreator,
    isPrintEnabled,
    makeRequest,
    locationId,
    suggestionFragment,
    jsonQuery,
    priceRange,
    hiddenOffers = [],
    hiddenSuggestions = [],
    isAgent,
    villagesLink,
    showCountrysideTrap,
    extendedOffersOffset,
    keyPrefix,
    isQaMode,
    isFilterByFlatsEnabled,
    realtyUserId,
    isCountrysideBuildersBannerAvailable,
    puidsByFilter,
    servicesBannerPositions: SERVICES_BANNER_POSITIONS,
    showSuburbanListingBanner,
    suburbanBuildersProjects,
  } = options;

  let positionOffset = options.positionOffset || 0;

  return offers.reduce((acc: React.ReactNode[], offer: IOffer, index) => {
    if (!hiddenOffers.includes(offer.id) && !hiddenSuggestions.includes(offer.id)) {
      const isOfferExtended = offer.extensionLabels && offer.extensionLabels.length;
      const position = (currentPageNumber - 1) * perPage + index;
      // порядок для события считаем начиная с единицы
      let trackingPosition = position + 1;

      // у добивок собственное начало отсчета
      trackingPosition = isOfferExtended ? trackingPosition - (extendedOffersOffset || 0) : trackingPosition;

      const nativeTgbPosition = trackingPosition + 1;

      const additionalPuids = {
        ...getAdditionalAdFoxPuids(jsonQuery),
        puid6: priceRange || '',
        ...(isQaMode ? { puid32: 'test' } : {}),
      };

      positionOffset += 1;

      const generateMortgageBanner = () =>
        addAdFoxBanner({
          type: 'mortgage_in_listing_new',
          key: `${keyPrefix}-mortgage_in_listing-${offer.id}`,
          makeRequest,
          offer,
          isAgent,
          realtyUserId,
          position: nativeTgbPosition,
          shouldSendAdsEvent: shouldSendAdsEvent(jsonQuery) || !isFilterByFlatsEnabled,
          isNew: true,
          width: '100%',
          minHeight: '275px',
          postViewAnalyticsData: {
            banner: {
              place: 'offersSerp',
              name: 'Listing_native_TGB',
              position: nativeTgbPosition,
              extra: {
                offerType: 'TGB_banner',
              },
            },
          },
        });

      const generateTgbBanner = () =>
        addAdFoxBanner({
          type: 'tgb_in_offers_list_new',
          key: `${keyPrefix}-tgb_in_offers_list-${offer.id}`,
          makeRequest,
          offer,
          position: nativeTgbPosition,
          locationId,
          isAgent,
          additionalPuids: {
            ...additionalPuids,
            ...getRailwayAndHighwayPuids(jsonQuery),
          },
          realtyUserId,
          shouldSendAdsEvent: shouldSendAdsEvent(jsonQuery) || !isFilterByFlatsEnabled,
          isNew: true,
          width: '100%',
          minHeight: '275px',
          postViewAnalyticsData: {
            banner: {
              place: 'offersSerp',
              name: 'Listing_native_TGB',
              position: nativeTgbPosition,
              extra: {
                offerType: 'TGB_banner',
              },
            },
          },
        });
      const generateParallaxBanner = () =>
        addAdFoxBanner({
          type: 'parallax',
          key: `${keyPrefix}-parallax-${offer.id}`,
          makeRequest,
          offer,
          locationId,
          isAgent,
          additionalPuids: {
            ...additionalPuids,
            ...getRailwayAndHighwayPuids(jsonQuery),
            ...puidsByFilter,
          },
          isNew: true,
          ignoreOfferPuids: true,
          shouldSendAdsEvent: shouldSendAdsEvent(jsonQuery) || !isFilterByFlatsEnabled,
          width: '100%',
          minHeight: '395px',
          postViewAnalyticsData: {
            banner: {
              place: 'offersSerp',
              name: 'Listing_parallax',
              position: nativeTgbPosition,
            },
          },
        });

      const generateSuburbanBanner = () =>
        addAdFoxBanner({
          type: 'suburban_listing_banner',
          key: `${keyPrefix}-suburban_listing_banner-${offer.id}`,
          makeRequest,
          offer,
          locationId,
          isAgent,
          realtyUserId,
          width: '100%',
          minHeight: '200px',
          isNew: true,
        });

      acc.push(
        offerCardCreator(
          offer,
          trackingPosition,
          suggestionFragment.isSimilar ? index + suggestionFragment.startIndex : index,
          currentPageNumber,
          keyPrefix,
        ),
      );

      if (!isPrintEnabled) {
        // MORTGAGE BANNER
        if (positionOffset === MORTGAGE_BANNER_POSITION_OFFSET) {
          acc.push(generateMortgageBanner());
        }

        // TGP BANNER
        if (
          positionOffset === TGP_BANNER_1_POSITION_OFFSET ||
          (positionOffset === TGP_BANNER_2_POSITION_OFFSET && offers.length > TGP_BANNER_2_POSITION_OFFSET)
        ) {
          acc.push(generateTgbBanner());
        }

        if (positionOffset === SUBURBAN_LISTING_BANNER_POSITION_OFFSET && showSuburbanListingBanner) {
          acc.push(generateSuburbanBanner());
        }

        // COUNTRYSIDE TRAP BANNER
        if (showCountrysideTrap && villagesLink && position === COUNTRYSIDE_TRAP_BANNER_POSITION) {
          acc.push(<CountrysideTrapBanner key={`countryside_banner_${position}`} villagesLink={villagesLink} />);
        }

        // SERVICES BANNER
        if (offers.length !== positionOffset && SERVICES_BANNER_POSITIONS.includes(position)) {
          acc.push(
            <BannerContainer
              key={`banner_${position}`}
              isAgent={isAgent}
              options={options}
              position={position}
              locationId={locationId}
              isCountrysideBuildersBannerAvailable={isCountrysideBuildersBannerAvailable}
            />,
          );
        }

        // PARALLAX BANNER
        if (
          positionOffset === PARALLAX_BANNER_1_POSITION_OFFSET ||
          positionOffset === PARALLAX_BANNER_2_POSITION_OFFSET
        ) {
          acc.push(generateParallaxBanner());
        }

        if (positionOffset === SUBURBAN_BUILDERS_PROJECTS_POSITION_OFFSET && suburbanBuildersProjects?.length) {
          acc.push(<SuburbanBuildersProjectsContainer />);
        }
      }
    }

    return acc;
  }, []);
};

export const getOnFavoriteChange =
  (
    offer: IOffer,
    jsonQuery: IJsonQuery,
    position: number,
    onFavoriteChangedHandler: (offer: IOffer, isFavorite: boolean) => void,
  ) =>
  (isFavorite: boolean) => {
    const trackingLabel = offer.gaLabel;

    if (isFavorite) {
      const kpAdvantages = isSaleFromSuburbanBuilder(offer) ? getOfferKpAdvantages(offer) : undefined;

      trackFavoriteAdded(jsonQuery, { offerId: offer.id, position, kpAdvantages }, trackingLabel || undefined);
    } else {
      trackFavoriteRemoved(jsonQuery, { offerId: offer.id, position }, trackingLabel || undefined);
    }

    onFavoriteChangedHandler(offer, isFavorite);
  };

export interface IOfferCardCreatorProps extends TOffersProps {
  offersQty: number;
  queryString: string;
  priceRange?: string;
  breadcrumbs: IBreadcrumb[];
  isAgent?: boolean;
}

interface IGetOfferCardCreator {
  props: IOfferCardCreatorProps;
  isSimilar?: boolean;
  isSimilarFromML?: boolean;
  pageNum?: number;
  isExtended?: boolean;
}

export const getOfferCardCreator =
  ({ props, isSimilar, isSimilarFromML, pageNum }: IGetOfferCardCreator) =>
  // eslint-disable-next-line react/display-name
  (offer: IOffer, position: number, index: number, currentPageNumber: number, keyPrefix: string): React.ReactNode => {
    const {
      breadcrumbs,
      currentPath,
      onFavoriteChanged,
      makeRequest,
      isPrintEnabled,
      jsonQuery,
      onUserInfoPopupClose,
      onUserInfoPopupOpen,
      hideOfferAction,
      qsToUris,
      suggestionDistancesSeoText,
      user,
      offersQty,
      mlRankingGuid,
      mlRankingModelVersion,
      queryString,
      priceRange,
      hideOffer: { hideOfferState, hidingOffer },
      maxAuctionBet,
      maxAuctionService,
      isAgent,
      isQaMode,
      trackingData,
      auctionBanks,
      villagePromotionLabel,
      addToComparison,
      deleteFromComparison,
      openChat,
      openNotAvailablePopup,
    } = props;

    const { id, bargainTerms } = offer;
    const withAuction = Boolean(
      !!auctionBanks && !!offer.user && !!offer.user.cianUserId && auctionBanks.includes(+offer.user.cianUserId),
    );
    const additionalPuids = {
      ...getAdditionalAdFoxPuids(jsonQuery),
      puid6: priceRange || '',
      puid32: isQaMode ? 'test' : '',
      puid38: String(offer?.user?.cianUserId || ''),
    };

    const onPopupClose = () => onUserInfoPopupClose(offer.id);
    const onPopupOpen = () => onUserInfoPopupOpen(offer.id);
    const onHideOffer =
      hideOfferState === EHideOfferState.FETCHING || hideOfferState === EHideOfferState.ANIMATING
        ? () => {}
        : () => {
            trackHiddenObjects({ action: 'Click_icon', offer, jsonQuery });
            hideOfferAction({ offer, isSimilar: !!isSimilar });
          };

    const mortgageBanner =
      bargainTerms && bargainTerms.mortgageAllowed && !isPrintEnabled
        ? addAdFoxBanner({
            type: props.featureToggle.isNewMortgageBannerEnabled ? 'mortgage_banner_new' : 'mortgage_banner',
            key: `${keyPrefix}-mortgage_banner-${id}`,
            makeRequest,
            offer,
            additionalPuids,
            isAgent,
            isNew: props.featureToggle.isNewMortgageBannerEnabled,
            width: '100%',
            minHeight: '20px',
          })
        : null;

    const onPageJumped = (label: string) =>
      trackPageJumped(offer, {
        user,
        jsonQuery,
        position,
        isSimilarFromML,
        label,
        pageNum,
        breadcrumbs,
        queryString,
        offersQty,
        mlRankingGuid,
        mlRankingModelVersion,
      });

    const offerCardProps = prepareOfferCardComponentsProps(props, {
      mortgageBanner,
      onPageJumped,
      onPopupClose,
      onPopupOpen,
      onHideOffer,
      onFavoriteChange: getOnFavoriteChange(offer, jsonQuery, position, onFavoriteChanged),
      trackPhoneOpened: (isFromBrandBlock?: boolean) =>
        trackPhoneOpened(offer, position, jsonQuery, user, offersQty, queryString, isSimilar, isFromBrandBlock),
      offer,
      jsonQuery,
      position,
      addToComparison,
      deleteFromComparison,
    });

    return (
      <OfferContainer
        key={`${keyPrefix}-${id}`}
        index={index}
        currentPath={currentPath}
        suggestionDistancesSeoText={suggestionDistancesSeoText}
        position={position}
        trackingData={trackingData}
        queryString={queryString}
        offer={offer}
        jsonQuery={jsonQuery}
        qsToUris={qsToUris}
        user={user}
        mortgageBanner={mortgageBanner}
        excludedUtilitiesTermsRegions={offerCardProps.excludedUtilitiesTermsRegions}
        maxAuctionBet={maxAuctionBet}
        maxAuctionService={maxAuctionService}
        isHideOfferInProgress={
          (Boolean(hidingOffer && hidingOffer.offer.id === offer.id) && hideOfferState === EHideOfferState.FETCHING) ||
          (Boolean(hidingOffer && hidingOffer.offer.id === offer.id) && hideOfferState === EHideOfferState.ANIMATING)
        }
        api={{
          onAgentRatingChanged: offerCardProps.userInfo.onAgentRatingChanged,
          onPageJumped,
          onHideOffer: offerCardProps.buttons.onHideOffer,
          isPopupsOpened: props.isPopupsOpened,
          onFavoritesChange: offerCardProps.offerCard.onFavoriteChange,
          sendComplaintFeedback: offerCardProps.complaintsHelpers.sendComplaintFeedback,
          onComplaintSent: offerCardProps.reportPopup.onComplaintSent,
          addToComparison: offerCardProps.offerComparison.addToComparison,
          deleteFromComparison: offerCardProps.offerComparison.deleteFromComparison,
          openChat,
          openNotAvailablePopup,
        }}
        isBot={offerCardProps.pessimisation.isBot}
        tracking={offerCardProps.tracking}
        featureToggle={offerCardProps.featureToggle}
        withAuction={withAuction}
        isDealRentDisablePhonesExperimentActive={Boolean(
          offer.offerInDeal && isDealRentDisablePhonesExperimentActive(props.abUseExperiments),
        )}
        isRosreestrCheckedLabelAvailable={isRosreestrCheckedLabelAvailable(offer)}
        isNewbuildingBookedFromDeveloper={isNewbuildingBookedFromDeveloper(offer)}
        villagePromotionLabel={villagePromotionLabel}
      />
    );
  };

export function getAdditionalAdFoxPuids(jsonQuery: IJsonQuery): { [key: string]: string } {
  const puid9 = (
    (jsonQuery.geo &&
      jsonQuery.geo.value
        .filter(geo => geo.type === 'underground')
        .map((underground: IJsonQueryUnderground) => underground.id)) ||
    []
  ).join(':');

  return {
    puid9,
  };
}

export function getRailwayAndHighwayPuids(jsonQuery: IJsonQuery): { [key: string]: string } {
  return jsonQuery.geo && jsonQuery.geo.value
    ? jsonQuery.geo.value.reduce(
        (acc, next) => {
          if (next.type === 'railway' && jsonQuery._type.includes('suburban')) {
            acc.puid34 = acc.puid34 ? `${acc.puid34}:${next.id}` : String(next.id);
          }

          if (next.type === 'highway' && jsonQuery._type.includes('suburban')) {
            acc.puid35 = acc.puid35 ? `${acc.puid35}:${next.id}` : String(next.id);
          }

          return acc;
        },
        { puid34: '', puid35: '' },
      )
    : {};
}

export function getOfferPrice(offer: IOffer) {
  let price = offer.minPriceTotalRur || offer.bargainTerms.priceRur;

  if (offer.bargainTerms.utilitiesTerms && !offer.bargainTerms.utilitiesTerms.includedInPrice) {
    price += offer.bargainTerms.utilitiesTerms.price;
  }

  return price;
}
